import { CategoryQuestions } from '../types';

export const sleepQuestions: CategoryQuestions = {
  title: 'Personalize Your Sleep Session',
  questions: {
    sessionType: {
      label: 'What type of sleep session would you prefer?',
      type: 'radio',
      options: ['Traditional Sleep Meditation', 'Bedtime Story Session', 'Educational & Learning Session']
    },
    preferredStyle: {
      label: 'What type of sleep assistance works best for you?',
      type: 'radio',
      options: ['Sleep stories with narrative', 'Progressive muscle relaxation', 'Breathing exercises for sleep', 'Nature sounds with gentle guidance']
    },
    storyAudience: {
      label: 'Who is this bedtime story for?',
      type: 'select',
      options: ['Child (ages 3-7)', 'Child (ages 8-12)', 'Teen', 'Adult'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    educationalAge: {
      label: 'What is the child\'s age range?',
      type: 'radio',
      options: ['2-4 years (Toddlers)', '5-7 years (Early Elementary)', '8-10 years (Elementary)', '11-13 years (Pre-teens)'],
      conditional: {
        field: 'sessionType',
        value: 'Educational & Learning Session'
      }
    },
    educationalTopic: {
      label: 'What would you like your child to learn about?',
      type: 'select',
      options: [
        'Learning the alphabet (A-Z)',
        'Counting numbers (1-20)',
        'Colors and shapes',
        'Days of the week',
        'Seasons and weather',
        'Exploring animals and habitats',
        'How plants grow',
        'The solar system and planets',
        'Oceans and sea creatures',
        'Kindness and sharing',
        'Being brave and confident',
        'Taking care of the environment',
        'Other/Custom topic'
      ],
      conditional: {
        field: 'sessionType',
        value: 'Educational & Learning Session'
      }
    },
    customEducationalTopic: {
      label: 'Describe the custom educational topic',
      type: 'textarea',
      placeholder: 'Example: "Learning about my grandmother who passed away" or "Understanding why we recycle"',
      max: 200,
      conditional: {
        field: 'educationalTopic',
        value: 'Other/Custom topic'
      }
    },
    storyTheme: {
      label: 'What story theme would you prefer?',
      type: 'radio',
      options: ['Adventure & Exploration', 'Animals & Nature', 'Fantasy & Magic', 'Real-world & Everyday', 'Educational & Learning', 'Family & Friendship'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    includePersonalDetails: {
      label: 'Include personal details in the story?',
      type: 'radio',
      options: ['Yes - Use my pets, family, hobbies', 'Yes - Use my favorite places', 'Yes - Use both personal details and places', 'No - Keep it general'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    dreamHeroType: {
      label: 'Who should be the hero of this story?',
      type: 'select',
      options: ['Superhero', 'Princess/Prince', 'Animal Friend', 'Space Explorer', 'Pirate', 'Dinosaur', 'Magical Creature'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    dreamWorldSetting: {
      label: 'Where does the adventure take place?',
      type: 'select',
      options: ['Magical Forest', 'Underwater Ocean', 'Outer Space', 'Enchanted Castle', 'Jungle Adventure', 'Snowy Mountains', 'Desert Island'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    dreamJourneyGoal: {
      label: 'What is the hero trying to do?',
      type: 'select',
      options: ['Find hidden treasure', 'Save a friend', 'Discover new worlds', 'Meet magical creatures', 'Solve a mystery', 'Go on an adventure'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    primaryGoal: {
      label: 'What is your main sleep challenge?',
      type: 'radio',
      options: ['Trouble falling asleep', 'Waking up frequently', 'Racing thoughts at bedtime', 'Stress and anxiety before sleep']
    },
    specificFocus: {
      label: 'What thoughts or worries keep you up tonight?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  // Practitioner perspective questions
  practitionerTitle: 'Personalize Sleep Session for Your Client',
  practitionerQuestions: {
    sessionType: {
      label: 'What type of sleep session would your client prefer?',
      type: 'radio',
      options: ['Traditional Sleep Meditation', 'Bedtime Story Session', 'Educational & Learning Session']
    },
    preferredStyle: {
      label: 'What type of sleep assistance works best for your client?',
      type: 'radio',
      options: ['Sleep stories with narrative', 'Progressive muscle relaxation', 'Breathing exercises for sleep', 'Nature sounds with gentle guidance']
    },
    storyAudience: {
      label: 'Who is this bedtime story for?',
      type: 'select',
      options: ['Child (ages 3-7)', 'Child (ages 8-12)', 'Teen', 'Adult'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    educationalAge: {
      label: 'What is your client\'s child\'s age range?',
      type: 'radio',
      options: ['2-4 years (Toddlers)', '5-7 years (Early Elementary)', '8-10 years (Elementary)', '11-13 years (Pre-teens)'],
      conditional: {
        field: 'sessionType',
        value: 'Educational & Learning Session'
      }
    },
    educationalTopic: {
      label: 'What would your client like their child to learn about?',
      type: 'select',
      options: [
        'Learning the alphabet (A-Z)',
        'Counting numbers (1-20)',
        'Colors and shapes',
        'Days of the week',
        'Seasons and weather',
        'Exploring animals and habitats',
        'How plants grow',
        'The solar system and planets',
        'Oceans and sea creatures',
        'Kindness and sharing',
        'Being brave and confident',
        'Taking care of the environment',
        'Other/Custom topic'
      ],
      conditional: {
        field: 'sessionType',
        value: 'Educational & Learning Session'
      }
    },
    customEducationalTopic: {
      label: 'Describe the custom educational topic',
      type: 'textarea',
      placeholder: 'Example: "Learning about my grandmother who passed away" or "Understanding why we recycle"',
      max: 200,
      conditional: {
        field: 'educationalTopic',
        value: 'Other/Custom topic'
      }
    },
    storyTheme: {
      label: 'What story theme would your client prefer?',
      type: 'radio',
      options: ['Adventure & Exploration', 'Animals & Nature', 'Fantasy & Magic', 'Real-world & Everyday', 'Educational & Learning', 'Family & Friendship'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    includePersonalDetails: {
      label: 'Include personal details in the story for your client?',
      type: 'radio',
      options: ['Yes - Use client\'s pets, family, hobbies', 'Yes - Use client\'s favorite places', 'Yes - Use both personal details and places', 'No - Keep it general'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    dreamHeroType: {
      label: 'Who should be the hero of this story?',
      type: 'select',
      options: ['Superhero', 'Princess/Prince', 'Animal Friend', 'Space Explorer', 'Pirate', 'Dinosaur', 'Magical Creature'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    dreamWorldSetting: {
      label: 'Where does the adventure take place?',
      type: 'select',
      options: ['Magical Forest', 'Underwater Ocean', 'Outer Space', 'Enchanted Castle', 'Jungle Adventure', 'Snowy Mountains', 'Desert Island'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    dreamJourneyGoal: {
      label: 'What is the hero trying to do?',
      type: 'select',
      options: ['Find hidden treasure', 'Save a friend', 'Discover new worlds', 'Meet magical creatures', 'Solve a mystery', 'Go on an adventure'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    primaryGoal: {
      label: 'What is your client\'s main sleep challenge?',
      type: 'radio',
      options: ['Trouble falling asleep', 'Waking up frequently', 'Racing thoughts at bedtime', 'Stress and anxiety before sleep']
    },
    specificFocus: {
      label: 'What thoughts or worries keep your client up?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  practitionerGroupTitle: 'Personalize Sleep Session for Your Clients',
  practitionerGroupQuestions: {
    groupSessionType: {
      label: 'What type of group sleep session would work best for your clients?',
      type: 'radio',
      options: ['Traditional Group Sleep Meditation', 'Group Bedtime Story Session', 'Group Educational & Learning Session']
    },
    groupEducationalAge: {
      label: 'What is the age range for this group?',
      type: 'radio',
      options: ['2-4 years (Toddlers)', '5-7 years (Early Elementary)', '8-10 years (Elementary)', '11-13 years (Pre-teens)', 'Mixed ages'],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Educational & Learning Session'
      }
    },
    groupEducationalTopic: {
      label: 'What would you like the group to learn about?',
      type: 'select',
      options: [
        'Learning the alphabet (A-Z)',
        'Counting numbers (1-20)',
        'Colors and shapes',
        'Days of the week',
        'Seasons and weather',
        'Exploring animals and habitats',
        'How plants grow',
        'The solar system and planets',
        'Oceans and sea creatures',
        'Kindness and sharing',
        'Being brave and confident',
        'Taking care of the environment',
        'Other/Custom topic'
      ],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Educational & Learning Session'
      }
    },
    groupCustomEducationalTopic: {
      label: 'Describe the custom educational topic',
      type: 'textarea',
      placeholder: 'Example: "Learning about teamwork" or "Understanding emotions"',
      max: 200,
      conditional: {
        field: 'groupEducationalTopic',
        value: 'Other/Custom topic'
      }
    },
    sleepGroupType: {
      label: 'What type of group sleep session are you creating?',
      type: 'radio',
      options: ['Family bedtime routine', 'Couple\'s sleep session', 'Children\'s group bedtime', 'Roommates/shared space', 'Sleepover group', 'Retreat/camp group sleep']
    },
    groupStoryTheme: {
      label: 'What story theme works best for the group?',
      type: 'radio',
      options: ['Adventure suitable for all ages', 'Animals & Nature for everyone', 'Fantasy & Magic for the group', 'Family & Friendship stories', 'Educational stories for mixed ages', 'Gentle exploration tales'],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Bedtime Story Session'
      }
    },
    groupStoryAudience: {
      label: 'What age ranges are in the group?',
      type: 'radio',
      options: ['All adults', 'Adults and teenagers', 'Adults and young children (2-8)', 'Mixed ages including elderly', 'Children only (2-8)', 'Children only (9-15)'],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Bedtime Story Session'
      }
    },
    groupSleepChallenges: {
      label: 'What sleep challenges does the group face together?',
      type: 'textarea',
      placeholder: 'Describe shared sleep issues, environment factors, or bedtime struggles...'
    },
    environmentConsiderations: {
      label: 'What is the group sleep environment like?',
      type: 'radio',
      options: ['Shared bedroom/space', 'Adjacent rooms', 'Same house/building', 'Outdoor/camping setting', 'Hotel/travel accommodation']
    },
    ageRangeAccommodation: {
      label: 'What age ranges need to be accommodated?',
      type: 'radio',
      options: ['All adults', 'Adults and teenagers', 'Adults and young children', 'Mixed ages including elderly', 'Children only']
    },
    groupSleepStyle: {
      label: 'What approach works best for the group\'s bedtime?',
      type: 'radio',
      options: ['Synchronized group relaxation', 'Quiet individual practice together', 'Gentle story for all ages', 'Progressive relaxation for shared space']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your clients?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  groupTitle: 'Personalize Your Group Sleep Session',
  groupQuestions: {
    groupSessionType: {
      label: 'What type of group sleep session would you prefer?',
      type: 'radio',
      options: ['Traditional Group Sleep Meditation', 'Group Bedtime Story Session', 'Group Educational & Learning Session']
    },
    groupEducationalAge: {
      label: 'What is the age range for this group?',
      type: 'radio',
      options: ['2-4 years (Toddlers)', '5-7 years (Early Elementary)', '8-10 years (Elementary)', '11-13 years (Pre-teens)', 'Mixed ages'],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Educational & Learning Session'
      }
    },
    groupEducationalTopic: {
      label: 'What would you like the group to learn about?',
      type: 'select',
      options: [
        'Learning the alphabet (A-Z)',
        'Counting numbers (1-20)',
        'Colors and shapes',
        'Days of the week',
        'Seasons and weather',
        'Exploring animals and habitats',
        'How plants grow',
        'The solar system and planets',
        'Oceans and sea creatures',
        'Kindness and sharing',
        'Being brave and confident',
        'Taking care of the environment',
        'Other/Custom topic'
      ],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Educational & Learning Session'
      }
    },
    groupCustomEducationalTopic: {
      label: 'Describe the custom educational topic',
      type: 'textarea',
      placeholder: 'Example: "Learning about teamwork" or "Understanding emotions"',
      max: 200,
      conditional: {
        field: 'groupEducationalTopic',
        value: 'Other/Custom topic'
      }
    },
    sleepGroupType: {
      label: 'What type of group sleep session are you creating?',
      type: 'radio',
      options: ['Family bedtime routine', 'Couple\'s sleep session', 'Children\'s group bedtime', 'Roommates/shared space', 'Sleepover group', 'Retreat/camp group sleep']
    },
    groupStoryTheme: {
      label: 'What story theme works best for your group?',
      type: 'radio',
      options: ['Adventure suitable for all ages', 'Animals & Nature for everyone', 'Fantasy & Magic for the group', 'Family & Friendship stories', 'Educational stories for mixed ages', 'Gentle exploration tales'],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Bedtime Story Session'
      }
    },
    groupStoryAudience: {
      label: 'What age ranges are in your group?',
      type: 'radio',
      options: ['All adults', 'Adults and teenagers', 'Adults and young children (2-8)', 'Mixed ages including elderly', 'Children only (2-8)', 'Children only (9-15)'],
      conditional: {
        field: 'groupSessionType',
        value: 'Group Bedtime Story Session'
      }
    },
    groupSleepChallenges: {
      label: 'What sleep challenges does your group face together?',
      type: 'textarea',
      placeholder: 'Describe shared sleep issues, environment factors, or bedtime struggles...'
    },
    environmentConsiderations: {
      label: 'What is your group sleep environment like?',
      type: 'radio',
      options: ['Shared bedroom/space', 'Adjacent rooms', 'Same house/building', 'Outdoor/camping setting', 'Hotel/travel accommodation']
    },
    ageRangeAccommodation: {
      label: 'What age ranges need to be accommodated?',
      type: 'radio',
      options: ['All adults', 'Adults and teenagers', 'Adults and young children', 'Mixed ages including elderly', 'Children only']
    },
    groupSleepStyle: {
      label: 'What approach works best for your group\'s bedtime?',
      type: 'radio',
      options: ['Synchronized group relaxation', 'Quiet individual practice together', 'Gentle story for all ages', 'Progressive relaxation for shared space']
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  giftTitle: 'Personalize Sleep Session for Your Loved One',
  giftQuestions: {
    sessionType: {
      label: 'What type of sleep session would they prefer?',
      type: 'radio',
      options: ['Traditional Sleep Meditation', 'Bedtime Story Session', 'Educational & Learning Session']
    },
    preferredStyle: {
      label: 'What type of sleep assistance works best for them?',
      type: 'radio',
      options: ['Sleep stories with narrative', 'Progressive muscle relaxation', 'Breathing exercises for sleep', 'Nature sounds with gentle guidance']
    },
    storyAudience: {
      label: 'Who is this bedtime story for?',
      type: 'select',
      options: ['Child (ages 3-7)', 'Child (ages 8-12)', 'Teen', 'Adult'],
      conditional: {
        field: 'sessionType',
        value: 'Bedtime Story Session'
      }
    },
    primaryGoal: {
      label: 'What is their main sleep challenge?',
      type: 'radio',
      options: ['Trouble falling asleep', 'Waking up frequently', 'Racing thoughts at bedtime', 'Stress and anxiety before sleep']
    },
    specificFocus: {
      label: 'What thoughts or worries keep them up?',
      type: 'textarea',
      placeholder: 'Optional - leave blank for general session'
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  }
};
